/**
 * The sprite units' programs (integration plan slice 3), after the item
 * billboards' pattern (`billboard.ts`):
 *
 * - **Frame lookup** (every sprite material, the `customDepthMaterial`
 *   included, so the cut-out shadow has the frame's outline): each instance
 *   carries its frame's rectangle in the sheet's atlas (u and v of the
 *   top-left, width, height; the atlas is uploaded with `flipY` false, so v
 *   grows downward) and a flip sign (-1 draws the frame mirrored: the
 *   contract's mirrored facings). The vertex program maps the plane's texture
 *   coordinates into that rectangle.
 * - **Mix** (the colour materials): each instance carries a colour and an
 *   amount (`abMix`: rgb, a), and the frame's colour is a lerp toward it, as
 *   a proxy's colour is: the gestures' flashes (a glow toward `palette.hit`,
 *   `heal`, `cast`), the missing-faint dim and the escape's fade toward the
 *   background. Off mixes the unlit colour; Medium mixes the lit result (the
 *   light the frame receives gives way to the colour as emitted light), so a
 *   flash glows rather than tints.
 * - **Cast** (the depth material): an instance with `abCast` 0 (faded past
 *   `presentation.sprite.shadowFadeCut`) is moved out of the shadow pass's
 *   clip space, so it casts nothing.
 */
export const spriteVertexHeader = 'attribute vec4 abFrame;attribute float abFlip;';

/** After `uv_vertex`: the map coordinates of this instance's frame. */
export const spriteUv = `
#ifdef USE_MAP
vMapUv=vec2(abFrame.x+(abFlip<0.0?1.0-uv.x:uv.x)*abFrame.z,abFrame.y+(1.0-uv.y)*abFrame.w);
#endif`;

export const spriteMixVertexHeader = 'attribute vec4 abMix;varying vec4 vAbMix;';
export const spriteMixFragmentHeader = 'varying vec4 vAbMix;';
/** After `uv_vertex`: the instance's mix colour and amount, to the fragment program. */
export const spriteMixVertex = 'vAbMix=abMix;';
/** After `color_fragment` (Off): the unlit colour lerps toward the mix colour. */
export const spriteMixUnlit = 'diffuseColor.rgb=mix(diffuseColor.rgb,vAbMix.rgb,vAbMix.a);';
/** Before `aomap_fragment` (Medium, after every light is in): the lit result lerps toward the mix colour, emitted. */
export const spriteMixLit = `
{float abKeep=1.0-vAbMix.a;
reflectedLight.directDiffuse*=abKeep;reflectedLight.indirectDiffuse*=abKeep;
reflectedLight.directSpecular*=abKeep;reflectedLight.indirectSpecular*=abKeep;
totalEmissiveRadiance=totalEmissiveRadiance*abKeep+vAbMix.rgb*vAbMix.a;}`;

export const spriteCastVertexHeader = 'attribute float abCast;';
/** After `project_vertex` (the depth program): an instance that casts no shadow leaves clip space. */
export const spriteCastVertex = 'if(abCast<0.5)gl_Position=vec4(0.0,0.0,2.0,1.0);';
